import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ProductDetails } from "@/components/product/ProductSections";
import { useFeatures } from "@/lib/features";
import { trackEvent } from "@/lib/analytics";
import { trackProductView } from "@/lib/attribution";
import Seo from "@/components/Seo";
import { ShopBuyModal } from "@/components/ShopBuyModal";

const SITE = "https://voluntoldhq.com";
const KEY = "coach_ready";
const TOOLKIT = "coach_ready_toolkit";
const LESSONS = ["Your first day", "Coaching different ages", "Practices", "Confidence and playing time", "Parents", "Team culture"];

const ld = (p) => ({
  "@context": "https://schema.org", "@type": "Product", name: p.name, description: p.blurb, sku: KEY,
  brand: { "@type": "Brand", name: "VoluntoldHQ" },
  offers: { "@type": "Offer", price: (p.amount / 100).toFixed(2), priceCurrency: "USD", availability: "https://schema.org/InStock", url: `${SITE}/coach-ready` },
});

const LessonList = () => (
  <ol className="mt-3 grid sm:grid-cols-2 gap-2" data-testid="coach-ready-lessons">
    {LESSONS.map((t, i) => (
      <li key={t} className="flex items-center gap-3 border-2 border-[#D3CFC0] bg-white px-3 py-2.5 text-sm text-[#1E1E1E]">
        <span className="font-head text-lg w-6 text-center" style={{ color: "#DB9130" }}>{i + 1}</span>{t}
      </li>
    ))}
  </ol>
);

export default function CoachReady() {
  const features = useFeatures();
  const [p, setP] = useState(null);
  const [all, setAll] = useState([]);
  const [kit, setKit] = useState(null);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/products").then(({ data }) => {
      setAll(data || []);
      setP((data || []).find((x) => x.lookup_key === KEY) || null);
      setKit((data || []).find((x) => x.lookup_key === TOOLKIT) || null);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!p) return;
    trackProductView(api, KEY);
    trackEvent("view_item", { currency: "USD", value: p.amount / 100, items: [{ item_id: KEY, item_name: p.name }] });
  }, [p]);

  if (features && !features.coach_ready) return <Navigate to="/shop" replace />;
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16" data-testid="coach-ready-page">
      {p && <Seo title={p.name} brand="VoluntoldHQ" description={p.blurb} path="/coach-ready" type="product" jsonLd={ld(p)} />}
      <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-14 items-start">
        <div>
          <span className="kicker" style={{ color: "#DB9130" }}>Online program · Coaches</span>
          <h1 className="mt-2 font-head font-semibold uppercase text-4xl sm:text-5xl lg:text-6xl text-[#1E1E1E] leading-[1.02]" data-testid="coach-ready-title">Coach Ready</h1>
          <p className="mt-5 text-base md:text-lg text-[#3A3A3A] leading-relaxed max-w-xl" data-testid="coach-ready-blurb">{p?.blurb}</p>
        </div>
        <div className="space-y-5 lg:sticky lg:top-24">
          <div className="border-2 border-[#1E1E1E] bg-[#1E1E1E] text-[#F5F0EA] p-6 sm:p-8" data-testid="coach-ready-buy-box">
            <span className="kicker" style={{ color: "#DB9130" }}>One-time payment</span>
            <div className="mt-2 font-head text-5xl" data-testid="coach-ready-price">${p ? p.amount / 100 : 49}</div>
            <p className="mt-3 text-sm text-[#D8D3C7] leading-relaxed">Private access link by email right after checkout. No account to set up. Work through it on your phone, at your own pace.</p>
            <button onClick={() => p && setBuying(true)} disabled={!p} data-testid="coach-ready-buy-btn"
              className="mt-6 w-full bg-[#E8A33D] text-[#1A1A1A] font-head uppercase tracking-wide text-sm px-8 py-3.5 hover:bg-[#D6922C] transition-colors disabled:opacity-60">
              Get Coach Ready
            </button>
          </div>
          {kit && (
            <Link to={`/shop/${TOOLKIT}`} className="block border-2 border-[#D3CFC0] bg-white p-5 hover:border-[#1E1E1E] transition-colors" data-testid="coach-ready-toolkit-card">
              <span className="text-xs uppercase tracking-wide font-bold" style={{ color: "#DB9130" }}>Optional add-on · ${kit.amount / 100}</span>
              <span className="block mt-1 font-head uppercase text-lg text-[#1E1E1E]">{kit.name}</span>
              <span className="block text-sm text-[#3A3A3A]">{kit.blurb} You can add it at checkout.</span>
            </Link>
          )}
        </div>
      </div>
      {p && <ProductDetails p={p} products={all} insideBefore={<div className="mb-6"><h3 className="font-head uppercase text-lg text-[#1E1E1E]">The six lessons</h3><LessonList /></div>} />}
      {buying && p && <ShopBuyModal product={p} addon={kit} source="coach_ready_page" onClose={() => setBuying(false)} />}
    </div>
  );
}
